import {
  Box,
  FormControl,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { ChangeEvent } from 'react';
import { PaginationRenderItemParams } from '@mui/material/Pagination';

export function renderListPaginationItem(item: PaginationRenderItemParams): JSX.Element {
  if (item.type === 'first' || item.type === 'last') {
    return (
      <PaginationItem
        {...item}
        type="page"
        page={item.type === 'first' ? 'First' : 'Last'}
        selected={false}
      />
    );
  }
  return <PaginationItem {...item} />;
}

export default function ListPagination({
  count,
  page,
  onChange,
}: {
  count: number;
  page: number;
  onChange: (event: ChangeEvent<unknown>, value: number) => void;
}): JSX.Element {
  return (
    <Pagination
      count={count}
      page={page}
      onChange={onChange}
      showFirstButton
      showLastButton
      renderItem={renderListPaginationItem}
      sx={{
        '& .MuiPagination-ul': { gap: '8px' },
        '& .MuiPaginationItem-root': {
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: 14,
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          color: '#111927',
          '&.Mui-selected': {
            backgroundColor: '#111927',
            color: '#FFFFFF',
            borderColor: '#111927',
            '&:hover': { backgroundColor: '#111927' },
          },
        },
      }}
    />
  );
}

const rowsSelectSx = {
  minWidth: 72,
  height: 36,
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  borderRadius: '8px',
  bgcolor: '#FFFFFF',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
};

export function ListPaginationBar({
  pageCount,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  total,
}: {
  pageCount: number;
  page: number;
  onPageChange: (event: ChangeEvent<unknown>, value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rows: number) => void;
  total: number;
}): JSX.Element {
  const rangeStart = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(page * rowsPerPage, total);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        pt: 2.5,
        mt: 1,
        borderTop: '1px solid #F2F4F7',
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            color: '#6B7280',
            whiteSpace: 'nowrap',
          }}>
          Rows per page
        </Typography>
        <FormControl size="small">
          <Select
            value={String(rowsPerPage)}
            onChange={(e: SelectChangeEvent) => onRowsPerPageChange(Number(e.target.value))}
            sx={rowsSelectSx}>
            {[10, 25, 50].map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            color: '#6B7280',
            whiteSpace: 'nowrap',
          }}>
          {rangeStart}-{rangeEnd} of {total}
        </Typography>
      </Box>
      <ListPagination count={pageCount} page={page} onChange={onPageChange} />
    </Box>
  );
}
